import { createEffect, createMemo, createSignal, type Accessor } from "solid-js"
import type { Nullable } from "vite-node"
import isIndexListSame from "../../../../rx/signal/equals/isIndexListSame"
import alwaysZero from "../../../ldsh/alwaysZero"
import noop from "../../../ldsh/noop"
import { immutableEmptyArray } from "../../../lib"
import dijkstraDirectionCharsLowerCase from "../../direction/dijkstraDirectionCharsLowerCase"
import { dijkstraDirections } from "../../direction/dijkstraDirections"
import dijkstraDirectionsLowerCase from "../../direction/dijkstraDirectionsLowerCase"
import getOppositeDirection from "../../direction/getOppositeDirection"
import type { EntityId } from "../../entity/EntityId.type"
import type { EntityTypeId } from "../../entity/EntityTypeId.enum"
import type { TileGraphNode } from "../TileGraphNode/TileGraphNode.type"
import type { TileGraphEdge } from "./TileGraphEdge.type"
import getTileGraphEdgeListSizeFromWidthHeight from "./getTileGraphEdgeListSizeFromWidthHeight"

type DirectionChar = 'n' | 's' | 'e' | 'w'

export default function createTileGraphEdgeCollection(
  nodeList: Array<TileGraphNode>,
  width: number,
  height: number
): Array<TileGraphEdge> {
  const len = getTileGraphEdgeListSizeFromWidthHeight(width, height)
  const list: Array<TileGraphEdge> = new Array(len)

  let edgeIndex = 0
  for (let nodeIndex = 0; nodeIndex < nodeList.length; nodeIndex++) {
    const node0: TileGraphNode = nodeList[nodeIndex]
    for (let dirIndex = 0; dirIndex < dijkstraDirectionsLowerCase.length; dirIndex++) {
      const dirCharLc = dijkstraDirectionsLowerCase[dirIndex]!
      const node1: TileGraphNode | null = node0[dirCharLc]
      if (!node0.edge[dirCharLc]) {
        const isHorizontal = dirCharLc === 'n' || dirCharLc === 's'
        // eslint-disable-next-line solid/reactivity
        const getFloorElevationDiff = node1 ? createMemo(() => {
          return Math.abs(node0.floor_e() - node1.floor_e())
        }) : alwaysZero
        // eslint-disable-next-line solid/reactivity
        const getMinimumFloorElevation = node1 ? createMemo(() => {
          return Math.min(node0.floor_e(), node1.floor_e())
        }) : node0.floor_e
        // eslint-disable-next-line solid/reactivity
        const getMaximumFloorElevation = node1 ? createMemo(() => {
          return Math.max(node0.floor_e(), node1.floor_e())
        }) : node0.floor_e

        const dxdy = dijkstraDirections[dirIndex]!

        /*
        * return 2 nodes in order,
        * if and only if they both exists
        * and are not equal
        */
        const getNodesDesc = createMemo(() => {
          if (node0 && node1) {
            const floorElevation0 = node0.floor_e()
            const floorElevation1 = node1.floor_e()
            if (floorElevation0 === floorElevation1) {
              return immutableEmptyArray as unknown as Array<TileGraphNode>
            }
            if (floorElevation0 > floorElevation1) {
              return [node0, node1]
            }
            return [node1, node0]
          }
          return immutableEmptyArray as unknown as Array<TileGraphNode>
        }, immutableEmptyArray as unknown as Array<TileGraphNode>, {
          equals: isIndexListSame,
        })

        // eslint-disable-next-line solid/reactivity
        const getRidgeDirectionIndex = node1 ? createMemo(() => {
          const [higherNode0, lowerNode1] = getNodesDesc()
          if (higherNode0 && lowerNode1) {            
            // If no exact match found, use the original edge direction
            // but adjust if the nodes are in reverse order
            if (higherNode0 === node1) {
              // If higher node is node1, we need the opposite direction
              const oppositeDir = getOppositeDirection(dijkstraDirectionCharsLowerCase, dirCharLc as DirectionChar)
              return dijkstraDirectionCharsLowerCase.indexOf(oppositeDir)
            }
            return dirIndex
          }
          return null
        }) : (noop as unknown as Accessor<Nullable<number>>)

        const l1 = {
          id: noop as unknown as Accessor<Nullable<EntityId>>,
          etype_id: noop as unknown as Accessor<Nullable<EntityTypeId>>,
        }
        if (node0 && node1) {
          const [getLayer1EntityIdOrNull, setLayer1EntityIdOrNull] = createSignal<Nullable<EntityId>>()
          const [getLayer1EntityTypeIdOrNull, setLayer1EntityTypeIdOrNull] = createSignal<Nullable<EntityTypeId>>()
          createEffect(() => {
            const [higherNode] = getNodesDesc()
            const getL1 : TileGraphNode['ents']['l1'] | undefined = higherNode?.ents?.l1
            setLayer1EntityIdOrNull(getL1?.id())
            setLayer1EntityTypeIdOrNull(getL1?.etype_id())
          })
          l1.id = getLayer1EntityIdOrNull
          l1.etype_id = getLayer1EntityTypeIdOrNull
        }

        const edge: TileGraphEdge = {
          index: edgeIndex,
          horz: isHorizontal,
          x: node0.x + (dxdy.dx / 2),
          y: node0.y + (dxdy.dy / 2),
          elevation: {
            diff: getFloorElevationDiff,
            max: getMaximumFloorElevation,
            min: getMinimumFloorElevation,
          },
          n: dirCharLc === 'n' ? node1 : (dirCharLc === 's' ? node0 : null),
          s: dirCharLc === 's' ? node1 : (dirCharLc === 'n' ? node0 : null),
          e: dirCharLc === 'e' ? node1 : (dirCharLc === 'w' ? node0 : null),
          w: dirCharLc === 'w' ? node1 : (dirCharLc === 'e' ? node0 : null),
          nodesDesc: getNodesDesc,
          rdi: getRidgeDirectionIndex,
          l1,
        }
        list[edgeIndex++] = edge
        node0.edge[dirCharLc] = edge
        if (node1) {
          const dirCharLcOpposite = getOppositeDirection(dijkstraDirectionsLowerCase, dirCharLc)
          node1.edge[dirCharLcOpposite] = edge
        }
      }
    }
  }

  // console.log('createTileGraphEdgeCollection', width, height, list.length, len)

  return list
}
